import { orderXClient } from '@/service/client'
import {
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  GetPlansResponse,
  GetSubscriptionResponse,
  OpenBillingPortalRequest,
  OpenBillingPortalResponse,
  ServerMessage,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

/**
 * Public plan catalog (`get_plans`). Unauthenticated — safe to render a pricing
 * page pre-login. Returns each plan's live Stripe prices and resolved
 * capabilities, plus the Stripe publishable key (our hosted checkout doesn't
 * need it; it's there for optional client-side Stripe widgets).
 */
export async function fetchPlans(): Promise<GetPlansResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getPlans: {} })
    if (data.error) return Promise.reject(data.error)
    return data.getPlansResponse ?? { plans: [], publishableKey: '' }
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * The caller's effective subscription (`get_subscription`). Returns the resolved
 * `planLevel`, the `planLabel` badge text, and the `capabilities` that drive all
 * feature gating — read these at runtime rather than hardcoding the tier table.
 * Requires authentication.
 */
export async function fetchSubscription(): Promise<GetSubscriptionResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getSubscription: {} })
    if (data.error) return Promise.reject(data.error)
    if (!data.getSubscriptionResponse) {
      return Promise.reject({ status: 500, message: 'Malformed subscription response' })
    }
    return data.getSubscriptionResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Start hosted Stripe Checkout (`create_checkout_session`). `priceId` comes from
 * `get_plans` (it selects plan + interval). `successUrl`/`cancelUrl` must be on
 * the caller's own origin so a shared backend returns the user to the site they
 * started on — there is no server default. Redirect the browser to the returned
 * `checkoutUrl`; the plan flips via webhook a beat after the redirect.
 */
export async function createCheckoutSession(
  payload: CreateCheckoutSessionRequest
): Promise<CreateCheckoutSessionResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      createCheckoutSession: payload,
    })
    if (data.error) return Promise.reject(data.error)
    if (!data.createCheckoutSessionResponse) {
      return Promise.reject({ status: 500, message: 'Malformed checkout response' })
    }
    return data.createCheckoutSessionResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Open the Stripe Billing Portal (`open_billing_portal`) where the user can
 * cancel, resume, switch interval, or update their card. `returnUrl` must be on
 * the caller's own origin. Redirect the browser to the returned `portalUrl`.
 */
export async function openBillingPortal(
  payload: OpenBillingPortalRequest
): Promise<OpenBillingPortalResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      openBillingPortal: payload,
    })
    if (data.error) return Promise.reject(data.error)
    if (!data.openBillingPortalResponse) {
      return Promise.reject({ status: 500, message: 'Malformed billing portal response' })
    }
    return data.openBillingPortalResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}
